import { useEffect, useState } from "react";
import { mediaUrl } from "../lib/media";

const initials = (name) => String(name || "?")
  .trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase() || "?";

export const playerPortraitUrl = (player) => mediaUrl(
  player?.portrait_image_url || player?.profile_image_url,
);

export const playerActionUrl = (player) => mediaUrl(player?.action_image_url);

export const focusCoordinate = (value, fallback = 50) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : fallback;
};

// A scale of 1 is deliberately the minimum: object-cover already fills every
// crop container, so smaller values could expose empty edges.
export const portraitZoom = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.min(2.5, parsed)) : fallback;
};

export const portraitFocusStyle = (player) => ({
  objectPosition: `${focusCoordinate(player?.portrait_focus_x)}% ${focusCoordinate(player?.portrait_focus_y)}%`,
});

export const portraitZoomStyle = (player) => ({
  transform: `scale(${portraitZoom(player?.portrait_zoom)})`,
  transformOrigin: `${focusCoordinate(player?.portrait_focus_x)}% ${focusCoordinate(player?.portrait_focus_y)}%`,
});

export const actionFocusStyle = (player) => ({
  objectPosition: `${focusCoordinate(player?.action_focus_x)}% ${focusCoordinate(player?.action_focus_y)}%`,
});

export function PlayerPortrait({ player, size = 48, height = size, shapeClassName = "rounded-full", className = "", imageClassName = "" }) {
  const source = playerPortraitUrl(player);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  const dimension = { width: size, height };
  if (source && !failed) {
    return <span className={`${shapeClassName} overflow-hidden shrink-0 block ${className}`} style={dimension}>
      <img src={source} alt={player?.name || ""} width={size} height={height}
        onError={() => setFailed(true)} className={`pc-player-portrait w-full h-full object-cover ${imageClassName}`}
        style={{ ...portraitFocusStyle(player), ...portraitZoomStyle(player) }}
        data-focus-x={focusCoordinate(player?.portrait_focus_x)}
        data-focus-y={focusCoordinate(player?.portrait_focus_y)}
        data-zoom={portraitZoom(player?.portrait_zoom)} data-testid="player-portrait-image" />
    </span>;
  }
  return <span className={`${shapeClassName} flex items-center justify-center font-display font-bold shrink-0 ${className}`}
    aria-label={`${player?.name || "Player"} profile fallback`} style={{
      ...dimension, background: "linear-gradient(145deg, var(--pc-charcoal-2), var(--pc-card-deep))",
      border: "1px solid var(--pc-border)", color: "var(--pc-muted)",
    }} data-testid="player-portrait-fallback">{initials(player?.name)}</span>;
}

export function PlayerActionImage({ player, className = "" }) {
  const source = playerActionUrl(player);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  return <div className={`pc-player-action-media ${className}`} data-testid="player-action-media">
    {source && !failed ? <img src={source} alt="" onError={() => setFailed(true)}
      className="pc-player-action-media__image" style={actionFocusStyle(player)}
      data-focus-x={focusCoordinate(player?.action_focus_x)} data-focus-y={focusCoordinate(player?.action_focus_y)}
      data-testid="player-action-image" />
      : <div className="pc-player-action-media__fallback" data-testid="player-action-fallback">
        <span className="pc-player-action-media__halo" aria-hidden="true" />
        <PlayerPortrait player={player} size={112} className="pc-player-action-media__portrait" />
        <span className="pc-player-action-media__monogram" aria-hidden="true">{initials(player?.name)}</span>
      </div>}
    <span className="pc-player-action-media__shade" aria-hidden="true" />
  </div>;
}
